/**
 * PaymentAnalyticsPage.js
 * Staff-facing payment analytics dashboard with KPIs, charts, aging report,
 * top payers, recent activity, and outstanding invoice detail.
 * Uses the same lightweight SVG chart components as BIDashboard (no external lib).
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  DollarSign, TrendingUp, CreditCard, Clock, Users, AlertTriangle,
  RefreshCw, Calendar, Download, ChevronDown, ChevronUp, Receipt,
  BarChart3, PieChart as PieIcon, ArrowUpRight, ArrowDownRight,
} from 'lucide-react';
import PrintButton from '../components/PrintButton';
import { NEU } from '../neuStyles';

// ─── Lightweight Chart Components ────────────────────────────────────────────

function BarChartSimple({ data, dataKey, nameKey = 'name', color = '#2E8B98', height = 200 }) {
  if (!data || data.length === 0) return <div style={{ color: '#ADB5BD', fontSize: 13, padding: 20 }}>No data available</div>;
  const max = Math.max(...data.map(d => d[dataKey] || 0), 1);
  return (
    <div style={{ height, display: 'flex', alignItems: 'flex-end', gap: 4, padding: '8px 0' }}>
      {data.map((d, i) => {
        const val = d[dataKey] || 0;
        const pct = (val / max) * 100;
        return (
          <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
            <span style={{ fontSize: 10, color: '#6C757D', fontWeight: 600 }}>{typeof val === 'number' && val >= 1000 ? `$${(val/1000).toFixed(1)}k` : formatCurrency(val)}</span>
            <div style={{
              width: '100%', maxWidth: 40, height: `${Math.max(pct, 2)}%`,
              background: color, borderRadius: '4px 4px 0 0', minHeight: 4,
              transition: 'height 0.3s ease',
            }} title={`${d[nameKey]}: ${formatCurrency(val)}`} />
            <span style={{ fontSize: 9, color: '#ADB5BD', textAlign: 'center', lineHeight: 1.2, maxWidth: 52, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {d[nameKey]}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function PieChartSimple({ data, height = 180 }) {
  if (!data || data.length === 0) return <div style={{ color: '#ADB5BD', fontSize: 13, padding: 20 }}>No data available</div>;
  const colors = ['#2E8B98', '#62BB46', '#F26522', '#DC3545', '#256F7A', '#14b8a6', '#8b5cf6', '#f59e0b'];
  const total = data.reduce((s, d) => s + d.value, 0) || 1;
  return (
    <div style={{ height, display: 'flex', gap: 16, alignItems: 'center' }}>
      <svg viewBox="0 0 100 100" style={{ width: height * 0.7, height: height * 0.7 }}>
        {data.reduce((acc, d, i) => {
          const pct = d.value / total;
          const startAngle = acc.offset;
          const endAngle = startAngle + pct * 360;
          const largeArc = pct > 0.5 ? 1 : 0;
          const rad1 = (startAngle - 90) * Math.PI / 180;
          const rad2 = (endAngle - 90) * Math.PI / 180;
          const x1 = 50 + 45 * Math.cos(rad1), y1 = 50 + 45 * Math.sin(rad1);
          const x2 = 50 + 45 * Math.cos(rad2), y2 = 50 + 45 * Math.sin(rad2);
          if (pct > 0.001) {
            acc.slices.push(
              <path key={i} d={`M50,50 L${x1},${y1} A45,45 0 ${largeArc},1 ${x2},${y2} Z`}
                fill={colors[i % colors.length]} stroke="#fff" strokeWidth="0.5">
                <title>{d.name}: {formatCurrency(d.value)} ({(pct * 100).toFixed(1)}%)</title>
              </path>
            );
          }
          acc.offset = endAngle;
          return acc;
        }, { slices: [], offset: 0 }).slices}
      </svg>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 5, fontSize: 12 }}>
        {data.slice(0, 8).map((d, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <div style={{ width: 10, height: 10, borderRadius: 2, background: colors[i % colors.length], flexShrink: 0 }} />
            <span style={{ color: '#212529' }}>{d.name}</span>
            <span style={{ color: '#ADB5BD', marginLeft: 'auto', fontWeight: 600 }}>{formatCurrency(d.value)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function LineChartSimple({ data, lines, height = 200, labelKey = 'month' }) {
  if (!data || data.length === 0) return <div style={{ color: '#ADB5BD', fontSize: 13, padding: 20 }}>No data available</div>;
  const colors = ['#2E8B98', '#62BB46', '#F26522', '#DC3545', '#256F7A'];
  const allVals = data.flatMap(d => lines.map(l => d[l] || 0));
  const max = Math.max(...allVals, 1);
  const w = 500, h = height - 40;
  const stepX = data.length > 1 ? w / (data.length - 1) : w;

  return (
    <div style={{ height, overflow: 'hidden' }}>
      <svg viewBox={`0 0 ${w + 40} ${h + 30}`} style={{ width: '100%', height: '100%' }}>
        {/* Grid lines */}
        {[0, 0.25, 0.5, 0.75, 1].map((pct, i) => (
          <line key={i} x1="30" y1={h - pct * (h - 10) + 5} x2={w + 30} y2={h - pct * (h - 10) + 5}
            stroke="#e5e7eb" strokeWidth="0.5" />
        ))}
        {lines.map((lineKey, li) => {
          const points = data.map((d, i) => {
            const x = 30 + i * stepX;
            const y = h - ((d[lineKey] || 0) / max) * (h - 10) + 5;
            return `${x},${y}`;
          }).join(' ');
          return <polyline key={lineKey} points={points} fill="none" stroke={colors[li % colors.length]} strokeWidth="2.5" strokeLinejoin="round" />;
        })}
        {data.map((d, i) => {
          const label = d[labelKey] || '';
          const shortLabel = labelKey === 'month' ? label.substring(5) : labelKey === 'day' ? label.substring(5) : label;
          return (
            <text key={i} x={30 + i * stepX} y={h + 18} textAnchor="middle" fontSize="8" fill="#ADB5BD">
              {shortLabel}
            </text>
          );
        })}
      </svg>
      <div style={{ display: 'flex', gap: 16, justifyContent: 'center', fontSize: 11, marginTop: -4 }}>
        {lines.map((l, i) => (
          <div key={l} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <div style={{ width: 14, height: 3, background: colors[i % colors.length], borderRadius: 1 }} />
            <span style={{ color: '#6C757D', textTransform: 'capitalize' }}>{l.replace(/([A-Z])/g, ' $1').trim()}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatCurrency(val) {
  if (val == null || isNaN(val)) return '$0.00';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val);
}

function formatDate(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

function formatDateTime(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

// ─── KPI Card ────────────────────────────────────────────────────────────────

function KPICard({ icon: Icon, label, value, subtitle, color = '#2E8B98', trend }) {
  return (
    <div style={{
      background: '#F8F9FA', borderRadius: 12, padding: '20px 24px', border: 'none',
      boxShadow: NEU.shadow.sm,
      flex: '1 1 180px', minWidth: 160, display: 'flex', flexDirection: 'column', gap: 8,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: 12, color: '#6C757D', fontWeight: 500 }}>{label}</span>
        <div style={{ width: 32, height: 32, borderRadius: 8, background: `${color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Icon size={16} color={color} />
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
        <div style={{ fontSize: 28, fontWeight: 700, color: '#212529', lineHeight: 1 }}>{value}</div>
        {trend != null && (
          <span style={{ display: 'flex', alignItems: 'center', fontSize: 12, fontWeight: 600, color: trend >= 0 ? '#059669' : '#dc2626' }}>
            {trend >= 0 ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}
            {Math.abs(trend).toFixed(1)}%
          </span>
        )}
      </div>
      {subtitle && <span style={{ fontSize: 11, color: '#ADB5BD' }}>{subtitle}</span>}
    </div>
  );
}

// ─── Card Wrapper ────────────────────────────────────────────────────────────

function ChartCard({ title, icon, children, className }) {
  return (
    <div style={{ ...NEU.card, padding: '1.25rem', marginBottom: '1rem', overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
        {icon}
        <h3 style={{ margin: 0, fontSize: '0.95rem', fontWeight: 700, color: '#111827' }}>{title}</h3>
      </div>
      {children}
    </div>
  );
}

// ─── Tab Button ──────────────────────────────────────────────────────────────

function TabBtn({ active, label, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: '6px 14px', fontSize: '0.8rem', fontWeight: active ? 700 : 500,
        color: active ? '#2E8B98' : '#6C757D',
        background: active ? '#2E8B9818' : 'transparent',
        border: 'none', borderRadius: 6, cursor: 'pointer',
        transition: 'all 0.15s',
      }}
    >
      {label}
    </button>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
//  Main Component
// ═══════════════════════════════════════════════════════════════════════════════

export default function PaymentAnalyticsPage({ user }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Data
  const [summary, setSummary] = useState(null);
  const [revenueTrend, setRevenueTrend] = useState([]);
  const [dailyTrend, setDailyTrend] = useState([]);
  const [methodBreakdown, setMethodBreakdown] = useState([]);
  const [aging, setAging] = useState([]);
  const [topPayers, setTopPayers] = useState([]);
  const [recentPayments, setRecentPayments] = useState([]);
  const [outstanding, setOutstanding] = useState([]);

  // Filters
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [trendView, setTrendView] = useState('monthly'); // monthly | daily
  const [activeTab, setActiveTab] = useState('overview'); // overview | aging | recent | outstanding

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [sum, rev, daily, methods, agingData, top, recent, outst] = await Promise.all([
        window.zignagenixAPI.payAnalyticsSummary(dateFrom || null, dateTo || null),
        window.zignagenixAPI.payAnalyticsRevenueTrend(12),
        window.zignagenixAPI.payAnalyticsDailyTrend(30),
        window.zignagenixAPI.payAnalyticsMethodBreakdown(dateFrom || null, dateTo || null),
        window.zignagenixAPI.payAnalyticsAging(),
        window.zignagenixAPI.payAnalyticsTopPayers(10),
        window.zignagenixAPI.payAnalyticsRecent(25),
        window.zignagenixAPI.payAnalyticsOutstanding(),
      ]);

      if (sum?.error) throw new Error(sum.message);
      setSummary(sum);
      setRevenueTrend(Array.isArray(rev) ? rev : []);
      setDailyTrend(Array.isArray(daily) ? daily : []);
      setMethodBreakdown(Array.isArray(methods) ? methods : []);
      setAging(Array.isArray(agingData) ? agingData : []);
      setTopPayers(Array.isArray(top) ? top : []);
      setRecentPayments(Array.isArray(recent) ? recent : []);
      setOutstanding(Array.isArray(outst) ? outst : []);
    } catch (err) {
      console.error('[PayAnalytics] load error:', err);
      setError(err.message || 'Failed to load analytics data.');
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo]);

  useEffect(() => { loadData(); }, [loadData]);

  // ── CSV Export ──
  function exportCSV(data, filename, headers) {
    if (!data?.length) return;
    const keys = headers ? Object.keys(headers) : Object.keys(data[0]);
    const headerRow = headers ? Object.values(headers).join(',') : keys.join(',');
    const rows = data.map(r => keys.map(k => {
      const v = r[k];
      return typeof v === 'string' && v.includes(',') ? `"${v}"` : (v ?? '');
    }).join(','));
    const csv = [headerRow, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  }

  // ── RENDER ──

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center', color: '#6C757D' }}>
        <RefreshCw size={28} className="spin" style={{ marginBottom: '0.75rem' }} />
        <p style={{ fontWeight: 600 }}>Loading Payment Analytics…</p>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } } .spin { animation: spin 1s linear infinite; }`}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <AlertTriangle size={32} color="#DC3545" style={{ marginBottom: '0.75rem' }} />
        <p style={{ color: '#DC3545', fontWeight: 600 }}>{error}</p>
        <button onClick={loadData} style={{ ...NEU.btnPrimary, marginTop: '0.5rem' }}>Retry</button>
      </div>
    );
  }

  const agingColors = { 'Current': '#059669', '1–30 days': '#f59e0b', '31–60 days': '#F26522', '61–90 days': '#DC3545', '90+ days': '#7f1d1d' };
  const totalAgingAmount = aging.reduce((s, a) => s + a.amount, 0);

  return (
    <div style={{ padding: '1.5rem', maxWidth: 1200, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '0.75rem' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 700, color: '#111827', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <BarChart3 size={24} color="#2E8B98" /> Payment Analytics
          </h1>
          <p style={{ margin: '0.25rem 0 0', fontSize: '0.85rem', color: '#6C757D' }}>
            Financial overview and payment reporting
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Calendar size={14} color="#6C757D" />
            <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
              style={{ ...NEU.input, padding: '4px 8px', fontSize: '0.8rem', width: 130 }} />
            <span style={{ color: '#ADB5BD', fontSize: '0.8rem' }}>to</span>
            <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
              style={{ ...NEU.input, padding: '4px 8px', fontSize: '0.8rem', width: 130 }} />
          </div>
          <button onClick={loadData} style={{ ...NEU.btnGhost, padding: '6px 12px', display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.8rem' }}>
            <RefreshCw size={14} /> Refresh
          </button>
          <PrintButton />
        </div>
      </div>

      {/* KPI Cards */}
      {summary && (
        <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
          <KPICard icon={DollarSign} label="Total Collected" value={formatCurrency(summary.totalCollected)}
            subtitle={`${summary.totalPayments} payments`} color="#059669" />
          <KPICard icon={CreditCard} label="Portal Payments" value={formatCurrency(summary.portalCollected)}
            subtitle={`${summary.portalPayments} via patient portal`} color="#2E8B98" />
          <KPICard icon={AlertTriangle} label="Outstanding" value={formatCurrency(summary.totalOutstanding)}
            subtitle={`${summary.openInvoices} open invoices`} color="#DC3545" />
          <KPICard icon={Clock} label="Overdue" value={formatCurrency(summary.overdueAmount)}
            subtitle={`${summary.overdueCount} overdue`} color="#F26522" />
          <KPICard icon={TrendingUp} label="Avg Payment" value={formatCurrency(summary.avgPayment)}
            subtitle="Per transaction" color="#8b5cf6" />
        </div>
      )}

      {/* Tab navigation */}
      <div style={{ display: 'flex', gap: 4, marginBottom: '1rem', background: '#F8F9FA', borderRadius: 8, padding: 4, boxShadow: NEU.shadow.insetSm, width: 'fit-content' }}>
        <TabBtn active={activeTab === 'overview'} label="Overview" onClick={() => setActiveTab('overview')} />
        <TabBtn active={activeTab === 'aging'} label="A/R Aging" onClick={() => setActiveTab('aging')} />
        <TabBtn active={activeTab === 'recent'} label="Recent Activity" onClick={() => setActiveTab('recent')} />
        <TabBtn active={activeTab === 'outstanding'} label="Outstanding" onClick={() => setActiveTab('outstanding')} />
      </div>

      {/* ═══ OVERVIEW TAB ═══ */}
      {activeTab === 'overview' && (
        <>
          {/* Revenue Trend */}
          <ChartCard
            title={trendView === 'monthly' ? 'Monthly Revenue Trend (12 months)' : 'Daily Revenue (30 days)'}
            icon={<TrendingUp size={18} color="#2E8B98" />}
          >
            <div style={{ display: 'flex', gap: 6, marginBottom: '0.75rem' }}>
              <TabBtn active={trendView === 'monthly'} label="Monthly" onClick={() => setTrendView('monthly')} />
              <TabBtn active={trendView === 'daily'} label="Daily" onClick={() => setTrendView('daily')} />
            </div>
            {trendView === 'monthly' ? (
              <LineChartSimple
                data={revenueTrend}
                lines={['portalTotal', 'staffTotal']}
                height={220}
                labelKey="month"
              />
            ) : (
              <BarChartSimple data={dailyTrend} dataKey="total" nameKey="day" color="#2E8B98" height={200} />
            )}
          </ChartCard>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: '1rem' }}>
            {/* Payment Method Breakdown */}
            <ChartCard title="Payment Methods" icon={<PieIcon size={18} color="#62BB46" />}>
              <PieChartSimple data={methodBreakdown} height={200} />
            </ChartCard>

            {/* Top Payers */}
            <ChartCard title="Top Payers" icon={<Users size={18} color="#8b5cf6" />}>
              <div style={{ maxHeight: 220, overflowY: 'auto' }}>
                {topPayers.length === 0 ? (
                  <p style={{ color: '#ADB5BD', fontSize: 13 }}>No data available</p>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                        <th style={{ textAlign: 'left', padding: '6px 8px', color: '#6C757D', fontWeight: 600 }}>Patient</th>
                        <th style={{ textAlign: 'right', padding: '6px 8px', color: '#6C757D', fontWeight: 600 }}>Payments</th>
                        <th style={{ textAlign: 'right', padding: '6px 8px', color: '#6C757D', fontWeight: 600 }}>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {topPayers.map((p, i) => (
                        <tr key={i} style={{ borderBottom: '1px solid #f3f4f6' }}>
                          <td style={{ padding: '6px 8px', color: '#111827', fontWeight: 500 }}>{p.patientName}</td>
                          <td style={{ padding: '6px 8px', color: '#6C757D', textAlign: 'right' }}>{p.paymentCount}</td>
                          <td style={{ padding: '6px 8px', color: '#059669', textAlign: 'right', fontWeight: 700 }}>{formatCurrency(p.totalPaid)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </ChartCard>
          </div>
        </>
      )}

      {/* ═══ AGING TAB ═══ */}
      {activeTab === 'aging' && (
        <ChartCard title="Accounts Receivable Aging" icon={<Clock size={18} color="#F26522" />}>
          {aging.length === 0 ? (
            <p style={{ color: '#ADB5BD', fontSize: 13 }}>No outstanding balances</p>
          ) : (
            <>
              {/* Horizontal stacked bar */}
              <div style={{ marginBottom: '1.5rem' }}>
                <div style={{ display: 'flex', height: 32, borderRadius: 8, overflow: 'hidden', marginBottom: '0.5rem' }}>
                  {aging.map((a, i) => {
                    const pct = totalAgingAmount > 0 ? (a.amount / totalAgingAmount) * 100 : 0;
                    return (
                      <div key={i} style={{
                        width: `${Math.max(pct, 2)}%`, background: agingColors[a.bucket] || '#6C757D',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 10, color: '#fff', fontWeight: 700, transition: 'width 0.3s',
                      }} title={`${a.bucket}: ${formatCurrency(a.amount)} (${a.count} invoices)`}>
                        {pct > 8 ? `${pct.toFixed(0)}%` : ''}
                      </div>
                    );
                  })}
                </div>
                <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', justifyContent: 'center' }}>
                  {aging.map((a, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: '0.8rem' }}>
                      <div style={{ width: 10, height: 10, borderRadius: 2, background: agingColors[a.bucket] || '#6C757D' }} />
                      <span style={{ color: '#374151' }}>{a.bucket}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Detail table */}
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                    <th style={{ textAlign: 'left', padding: '8px 12px', color: '#6C757D', fontWeight: 600 }}>Aging Bucket</th>
                    <th style={{ textAlign: 'right', padding: '8px 12px', color: '#6C757D', fontWeight: 600 }}>Invoices</th>
                    <th style={{ textAlign: 'right', padding: '8px 12px', color: '#6C757D', fontWeight: 600 }}>Amount</th>
                    <th style={{ textAlign: 'right', padding: '8px 12px', color: '#6C757D', fontWeight: 600 }}>% of Total</th>
                  </tr>
                </thead>
                <tbody>
                  {aging.map((a, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid #f3f4f6' }}>
                      <td style={{ padding: '8px 12px', display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{ width: 10, height: 10, borderRadius: 2, background: agingColors[a.bucket] || '#6C757D' }} />
                        <span style={{ fontWeight: 600, color: '#111827' }}>{a.bucket}</span>
                      </td>
                      <td style={{ padding: '8px 12px', textAlign: 'right', color: '#374151' }}>{a.count}</td>
                      <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 700, color: agingColors[a.bucket] || '#374151' }}>{formatCurrency(a.amount)}</td>
                      <td style={{ padding: '8px 12px', textAlign: 'right', color: '#6C757D' }}>{totalAgingAmount > 0 ? ((a.amount / totalAgingAmount) * 100).toFixed(1) : 0}%</td>
                    </tr>
                  ))}
                  <tr style={{ borderTop: '2px solid #374151', fontWeight: 700 }}>
                    <td style={{ padding: '8px 12px', color: '#111827' }}>Total</td>
                    <td style={{ padding: '8px 12px', textAlign: 'right', color: '#111827' }}>{aging.reduce((s, a) => s + a.count, 0)}</td>
                    <td style={{ padding: '8px 12px', textAlign: 'right', color: '#DC3545' }}>{formatCurrency(totalAgingAmount)}</td>
                    <td style={{ padding: '8px 12px', textAlign: 'right', color: '#374151' }}>100%</td>
                  </tr>
                </tbody>
              </table>

              <button
                onClick={() => exportCSV(aging, 'ar-aging-report.csv', { bucket: 'Aging Bucket', count: 'Invoices', amount: 'Amount' })}
                style={{ ...NEU.btnGhost, marginTop: '1rem', padding: '6px 14px', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: 5 }}
              >
                <Download size={14} /> Export CSV
              </button>
            </>
          )}
        </ChartCard>
      )}

      {/* ═══ RECENT ACTIVITY TAB ═══ */}
      {activeTab === 'recent' && (
        <ChartCard title="Recent Payments" icon={<Receipt size={18} color="#2E8B98" />}>
          {recentPayments.length === 0 ? (
            <p style={{ color: '#ADB5BD', fontSize: 13 }}>No payments recorded</p>
          ) : (
            <>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem', minWidth: 600 }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                      <th style={{ textAlign: 'left', padding: '8px', color: '#6C757D', fontWeight: 600 }}>Date</th>
                      <th style={{ textAlign: 'left', padding: '8px', color: '#6C757D', fontWeight: 600 }}>Patient</th>
                      <th style={{ textAlign: 'left', padding: '8px', color: '#6C757D', fontWeight: 600 }}>Invoice</th>
                      <th style={{ textAlign: 'left', padding: '8px', color: '#6C757D', fontWeight: 600 }}>Method</th>
                      <th style={{ textAlign: 'right', padding: '8px', color: '#6C757D', fontWeight: 600 }}>Amount</th>
                      <th style={{ textAlign: 'left', padding: '8px', color: '#6C757D', fontWeight: 600 }}>Reference</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentPayments.map((p, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid #f3f4f6' }}>
                        <td style={{ padding: '8px', color: '#374151', whiteSpace: 'nowrap' }}>{formatDateTime(p.date)}</td>
                        <td style={{ padding: '8px', color: '#111827', fontWeight: 500 }}>{p.patientName || '—'}</td>
                        <td style={{ padding: '8px', color: '#6C757D' }}>#{p.invoiceNumber || '—'}</td>
                        <td style={{ padding: '8px' }}>
                          <span style={{
                            display: 'inline-block', padding: '2px 8px', borderRadius: 999, fontSize: '0.75rem', fontWeight: 600,
                            background: p.method === 'stripe_portal' ? '#dbeafe' : p.method === 'cash' ? '#dcfce7' : '#f3f4f6',
                            color: p.method === 'stripe_portal' ? '#1e40af' : p.method === 'cash' ? '#166534' : '#374151',
                          }}>
                            {(p.method || 'other').replace(/_/g, ' ')}
                          </span>
                        </td>
                        <td style={{ padding: '8px', textAlign: 'right', fontWeight: 700, color: '#059669' }}>{formatCurrency(p.amount)}</td>
                        <td style={{ padding: '8px', color: '#9CA3AF', fontSize: '0.75rem', fontFamily: 'monospace', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {p.reference || '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <button
                onClick={() => exportCSV(recentPayments, 'recent-payments.csv', {
                  date: 'Date', patientName: 'Patient', invoiceNumber: 'Invoice', method: 'Method', amount: 'Amount', reference: 'Reference'
                })}
                style={{ ...NEU.btnGhost, marginTop: '1rem', padding: '6px 14px', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: 5 }}
              >
                <Download size={14} /> Export CSV
              </button>
            </>
          )}
        </ChartCard>
      )}

      {/* ═══ OUTSTANDING TAB ═══ */}
      {activeTab === 'outstanding' && (
        <ChartCard title="Outstanding Invoices" icon={<AlertTriangle size={18} color="#DC3545" />}>
          {outstanding.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '2rem 0' }}>
              <DollarSign size={32} color="#059669" style={{ marginBottom: '0.5rem' }} />
              <p style={{ fontWeight: 600, color: '#059669', margin: 0 }}>All Invoices Paid</p>
            </div>
          ) : (
            <>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem', minWidth: 700 }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                      <th style={{ textAlign: 'left', padding: '8px', color: '#6C757D', fontWeight: 600 }}>Invoice</th>
                      <th style={{ textAlign: 'left', padding: '8px', color: '#6C757D', fontWeight: 600 }}>Patient</th>
                      <th style={{ textAlign: 'left', padding: '8px', color: '#6C757D', fontWeight: 600 }}>Status</th>
                      <th style={{ textAlign: 'left', padding: '8px', color: '#6C757D', fontWeight: 600 }}>Date</th>
                      <th style={{ textAlign: 'left', padding: '8px', color: '#6C757D', fontWeight: 600 }}>Due</th>
                      <th style={{ textAlign: 'right', padding: '8px', color: '#6C757D', fontWeight: 600 }}>Total</th>
                      <th style={{ textAlign: 'right', padding: '8px', color: '#6C757D', fontWeight: 600 }}>Paid</th>
                      <th style={{ textAlign: 'right', padding: '8px', color: '#6C757D', fontWeight: 600 }}>Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {outstanding.map((inv, i) => {
                      const isOverdue = inv.dueDate && new Date(inv.dueDate) < new Date();
                      return (
                        <tr key={i} style={{ borderBottom: '1px solid #f3f4f6', background: isOverdue ? '#fef2f220' : 'transparent' }}>
                          <td style={{ padding: '8px', fontWeight: 600, color: '#111827' }}>#{inv.invoiceNumber}</td>
                          <td style={{ padding: '8px', color: '#374151' }}>{inv.patientName || '—'}</td>
                          <td style={{ padding: '8px' }}>
                            <span style={{
                              display: 'inline-block', padding: '2px 8px', borderRadius: 999, fontSize: '0.75rem', fontWeight: 600,
                              background: inv.status === 'overdue' ? '#fee2e2' : inv.status === 'partial' ? '#fef3c7' : '#dbeafe',
                              color: inv.status === 'overdue' ? '#991b1b' : inv.status === 'partial' ? '#92400e' : '#1e40af',
                            }}>
                              {inv.status}
                            </span>
                          </td>
                          <td style={{ padding: '8px', color: '#6C757D', whiteSpace: 'nowrap' }}>{formatDate(inv.date)}</td>
                          <td style={{ padding: '8px', color: isOverdue ? '#dc2626' : '#6C757D', fontWeight: isOverdue ? 600 : 400, whiteSpace: 'nowrap' }}>
                            {formatDate(inv.dueDate)}
                          </td>
                          <td style={{ padding: '8px', textAlign: 'right', color: '#374151' }}>{formatCurrency(inv.total)}</td>
                          <td style={{ padding: '8px', textAlign: 'right', color: '#059669' }}>{formatCurrency(inv.amountPaid)}</td>
                          <td style={{ padding: '8px', textAlign: 'right', fontWeight: 700, color: '#dc2626' }}>{formatCurrency(inv.balance)}</td>
                        </tr>
                      );
                    })}
                    <tr style={{ borderTop: '2px solid #374151', fontWeight: 700 }}>
                      <td colSpan={5} style={{ padding: '8px', color: '#111827' }}>Total ({outstanding.length} invoices)</td>
                      <td style={{ padding: '8px', textAlign: 'right', color: '#374151' }}>{formatCurrency(outstanding.reduce((s, i) => s + i.total, 0))}</td>
                      <td style={{ padding: '8px', textAlign: 'right', color: '#059669' }}>{formatCurrency(outstanding.reduce((s, i) => s + i.amountPaid, 0))}</td>
                      <td style={{ padding: '8px', textAlign: 'right', color: '#dc2626' }}>{formatCurrency(outstanding.reduce((s, i) => s + i.balance, 0))}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <button
                onClick={() => exportCSV(outstanding, 'outstanding-invoices.csv', {
                  invoiceNumber: 'Invoice', patientName: 'Patient', status: 'Status', date: 'Date', dueDate: 'Due Date', total: 'Total', amountPaid: 'Paid', balance: 'Balance'
                })}
                style={{ ...NEU.btnGhost, marginTop: '1rem', padding: '6px 14px', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: 5 }}
              >
                <Download size={14} /> Export CSV
              </button>
            </>
          )}
        </ChartCard>
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } } .spin { animation: spin 1s linear infinite; }`}</style>
    </div>
  );
}
